import { useState } from "react";
import { Banknote, Pencil } from "lucide-react";
import {
  useCanAccess,
  useCreate,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { ClientContract } from "../types";
import { formatDateOnly, formatMoney } from "./clientModel";
import { Section } from "./ClientShow";

// Contract terms are admin-only: the card is hidden for the team, and the
// client_contracts RLS policy refuses non-admin reads even if it were shown.
export const ClientContractCard = ({
  companyId,
}: {
  companyId: Identifier;
}) => {
  const { canAccess, isPending } = useCanAccess({
    resource: "client_contracts",
    action: "edit",
  });
  if (isPending || !canAccess) return null;
  return <ContractCardContent companyId={companyId} />;
};

const ContractCardContent = ({ companyId }: { companyId: Identifier }) => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const [isEditing, setIsEditing] = useState(false);
  const { data, isPending } = useGetList<ClientContract>("client_contracts", {
    filter: { company_id: companyId },
    pagination: { page: 1, perPage: 1 },
  });
  if (isPending) return null;
  const contract = data?.[0];

  return (
    <Section
      icon={<Banknote className="size-4" />}
      title={translate("resources.clients.sections.contract")}
    >
      {isEditing ? (
        <ContractForm
          companyId={companyId}
          contract={contract}
          onDone={() => setIsEditing(false)}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {contract?.monthly_fee != null ? (
            <div className="text-4xl font-semibold tracking-tight">
              {formatMoney(contract.monthly_fee, contract.currency, locale)}
              <span className="ml-1 text-base font-normal text-muted-foreground">
                / {translate("resources.clients.contract.per_month")}
              </span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {translate("resources.clients.contract.empty")}
            </p>
          )}
          {contract?.start_date ? (
            <p className="text-sm text-muted-foreground">
              {translate("resources.clients.contract.start_date")}:{" "}
              {formatDateOnly(contract.start_date, locale)}
            </p>
          ) : null}
          {contract?.notes ? (
            <p className="whitespace-pre-line text-sm">{contract.notes}</p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            {translate("resources.clients.contract.admin_only")}
          </p>
          <Button
            variant="outline"
            className="self-start rounded-full"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="size-4" />
            {translate("ra.action.edit")}
          </Button>
        </div>
      )}
    </Section>
  );
};

const ContractForm = ({
  companyId,
  contract,
  onDone,
}: {
  companyId: Identifier;
  contract?: ClientContract;
  onDone: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [create, { isPending: isCreating }] = useCreate();
  const [update, { isPending: isUpdating }] = useUpdate();
  const [monthlyFee, setMonthlyFee] = useState(
    contract?.monthly_fee != null ? String(contract.monthly_fee) : "",
  );
  const [startDate, setStartDate] = useState(contract?.start_date ?? "");
  const [notes, setNotes] = useState(contract?.notes ?? "");

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const parsedFee = Number(monthlyFee.replace(/[^\d.]/g, ""));
    const values = {
      company_id: companyId,
      monthly_fee: monthlyFee.trim() === "" ? null : parsedFee,
      start_date: startDate || null,
      notes: notes.trim() || null,
    };
    const options = {
      onSuccess: () => {
        notify("resources.clients.contract.saved", { type: "success" });
        onDone();
      },
      onError: () => notify("ra.notification.http_error", { type: "error" }),
    };
    if (contract) {
      update(
        "client_contracts",
        { id: contract.id, data: values, previousData: contract },
        options,
      );
    } else {
      create("client_contracts", { data: values }, options);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="monthly_fee">
          {translate("resources.clients.contract.monthly_fee")} (COP)
        </Label>
        <Input
          id="monthly_fee"
          inputMode="numeric"
          placeholder="3000000"
          value={monthlyFee}
          onChange={(event) => setMonthlyFee(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="start_date">
          {translate("resources.clients.contract.start_date")}
        </Label>
        <Input
          id="start_date"
          type="date"
          value={startDate}
          onChange={(event) => setStartDate(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="contract_notes">
          {translate("resources.clients.contract.notes")}
        </Label>
        <Textarea
          id="contract_notes"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>
          {translate("ra.action.cancel")}
        </Button>
        <Button type="submit" disabled={isCreating || isUpdating}>
          {translate("ra.action.save")}
        </Button>
      </div>
    </form>
  );
};
